import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { RosterPilotLogo } from '../../../assets/RosterPilotLogo';
import { useTravelBallAvailabilityStatus } from '../../../hooks/useTravelBallAvailability';
import './SignupChooserPage.css';

/**
 * Wraps the public travel ball signup pages (the chooser, program and family signup). They create the
 * Firebase account and send its verification email in the browser, before any API call, so the API's
 * `travelball.foundation` refusal alone would come too late: with the flag off a visitor would end up with an
 * account and "check your inbox" for a signup that can never finish. So the page itself waits for the anonymous
 * availability read and shows nothing of the form unless it answers yes. It fails closed, like the login link.
 */
export const RequireTravelBallSignupOpen = ({ children }: { children: ReactNode }) => {
  const status = useTravelBallAvailabilityStatus();

  if (status === 'open') return <>{children}</>;

  return (
    <main className="tb-signup-chooser">
      <div className="tb-signup-chooser__container">
        <div className="tb-signup-chooser__logo">
          <RosterPilotLogo />
        </div>
        {status === 'checking' ? (
          <p className="tb-signup-chooser__subtitle" role="status">
            Loading…
          </p>
        ) : (
          <>
            <h1 className="tb-signup-chooser__title">Travel ball signup isn't open yet</h1>
            <p className="tb-signup-chooser__subtitle" data-testid="tb-signup-closed">
              Check back soon. If a program sent you an invite, use the link in that email.
            </p>
            <p className="tb-signup-chooser__footnote">
              <Link to="/login" className="tb-signup-chooser__footnote-link">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </main>
  );
};
